import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { TokenStore } from '@axl/accel-framework/stores';
import { Alert, Button, Form, Input, Spin, Typography } from 'antd';
import { Rule } from "antd/lib/form";
import { SchoolDocTitle, useLoc, useRouter } from 'components';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from "react";
import { useInstance } from "react-ioc";
import { Link } from 'react-router-dom';
import { SchoolStore } from 'stores';
import AuthStore from 'views/auth/stores/AuthStore';
import authStyles from '../styles/auth.module.scss';
import loginStyles from './Login.module.scss';
import { FormBuilder } from '@axl/accel-framework/components';

declare type LoginActType = 'logout';
type LoginParams = {
    contactId: string;
    act: LoginActType,
    returnUrl: string;
}

const Login: React.FC = () => {
    const authStore = useInstance(AuthStore);
    const tokenStore = useInstance(TokenStore);
    const loc = useLoc();
    const schoolStore = useInstance(SchoolStore);
    const [form] = Form.useForm();
    const [emailDisabled] = useState(authStore.hasContactEmail);

    const { params, history, navigate } = useRouter<LoginParams>();

    useEffect(() => {
        authStore.setUrlParameters(params);
        if (params.act == 'logout')
            logout();
    }, [])

    const logout = useCallback(async () => {
        await authStore.logout();
        tokenStore.clearTokens();

        //if the app is from iframe, we need to send message to parent window that we are logged out
        //@ts-ignore
        if (window.ReactNativeWebView) {
            //@ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify({
                from: "axl",
                type: "logout",
            }), '*');
        }

        history.replace('/login');
    }, []);

    const login = useCallback(async () => {
        if (await authStore.login())
            navigate(params.returnUrl || '/');
    }, []);

    const passwordRules: Rule[] = [
        {
            required: true,
            message: loc.word('Please input your password')
        },
        {
            type: 'string',
            min: 6,
            message: loc.word('Password must be longer than 6 characters')
        },
        {
            type: 'string',
            max: 30,
            message: loc.word('Password must be shorter than 30 characters')
        }
    ];

    return <SchoolDocTitle title={loc.word('Login')}>
        <Typography.Title className={authStyles.title} level={3} data-testid='title'>{loc.word('Login')}</Typography.Title>
        <Spin tip={loc.word('Loading')} spinning={authStore.inProgress}>
            {authStore.errors.map(x => <Alert key={x.code} message={x.message} type={'error'}></Alert>)}
            <Form form={form}
                onFinish={() => login()}
                layout='vertical'
                initialValues={{ email: authStore.email }}
                validateTrigger='onBlur'>

                <FormBuilder.Input name='email'
                    label={loc.word('Email')}
                    rules={!emailDisabled ? x => x.required().email() : undefined}
                    itemProps={{ placeholder: loc.word('Enter email'), prefix: <UserOutlined />, tabIndex: 1, disabled: emailDisabled }}
                    onChange={e => {
                        authStore.setEmail(e.target.value);
                        form.setFieldsValue({ email: authStore.email });
                    }} />

                <Form.Item
                    label={<div className='flex justify-between align-center w-100'>
                        <span>{loc.word('Password')}</span>
                        <Link to='/forgot-password' data-testid='forgot-password-button'>{loc.word('Forgot password?')}</Link>
                    </div>}
                    name="password"
                    rules={passwordRules}
                    className={loginStyles.password_input}>
                    <Input.Password tabIndex={2}
                        prefix={<LockOutlined />}
                        type="password"
                        placeholder={loc.word('Enter your password')}
                        onChange={e => authStore.setPassword(e.target.value)}
                    />
                </Form.Item>
                <Form.Item className={authStyles.form_button_item}>
                    <Button type="primary" htmlType="submit" data-testid='submit-button'>
                        {loc.word('Log in')}
                    </Button>
                </Form.Item>
                {(!schoolStore.school.disableOpenRegistration && !authStore.hasContactEmail)
                    && <div className={authStyles.auth_form_wrapper_footer}>
                        {loc.word(`Don't have an account?`)}&nbsp;<Link to='/register' data-testid='register-button'>{loc.word('Register')}</Link>
                    </div>}
            </Form>
        </Spin>
    </SchoolDocTitle>;
}

export default observer(Login);